import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Shield, Plus, X, KeyRound, Trash2, Lock, Unlock } from "lucide-react";
import { api } from "@/lib/api";
import { Card, Badge, Btn, EmptyState, AInput, ASelect, ATextarea, useSave } from "../AdminUI";

function TempPassword({ pw }) {
  if (!pw) return null;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm" data-testid="temp-password">
      <p className="font-semibold text-amber-800">Temporary password (share securely — shown once):</p>
      <code className="text-base font-mono text-amber-900">{pw}</code>
    </div>
  );
}

function SectionPicker({ all, roleDefault, granted, denied, onChange }) {
  const toggle = (s) => {
    const inGrant = granted.includes(s), inDeny = denied.includes(s), inRole = roleDefault.includes(s);
    // cycle: default -> grant -> deny -> default
    if (!inGrant && !inDeny) onChange({ granted: inRole ? granted : [...granted, s], denied: inRole ? [...denied, s] : denied });
    else if (inGrant) onChange({ granted: granted.filter((x) => x !== s), denied: [...denied, s] });
    else onChange({ granted, denied: denied.filter((x) => x !== s) });
  };
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
      {all.map((s) => {
        const g = granted.includes(s), d = denied.includes(s), r = roleDefault.includes(s);
        const active = (r || g) && !d;
        return (
          <button key={s} type="button" onClick={() => toggle(s)} data-testid={`section-${s}`}
            className={`text-[11px] px-2 py-1.5 rounded border text-left truncate ${active ? "bg-green-50 border-green-300 text-green-800" : "bg-gray-50 border-gray-200 text-gray-400"}`}>
            {active ? "✓" : "○"} {s}{g && " +"}{d && " −"}
          </button>
        );
      })}
    </div>
  );
}

function UserModal({ user, meta, onClose, onSaved }) {
  const isNew = !user;
  const [f, setF] = useState(user || { email: "", full_name: "", phone: "", role: "Read Only", status: "Invited", notes: "", granted_sections: [], denied_sections: [] });
  const [temp, setTemp] = useState("");
  const [saving, run] = useSave();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const roleDefault = meta.role_sections[f.role]?.includes("*") ? meta.sections : (meta.role_sections[f.role] || []);

  const submit = (e) => { e.preventDefault(); run(async () => {
    if (isNew) {
      const r = await api.post("/admin/users", { ...f });
      if (r.data.temp_password) setTemp(r.data.temp_password);
      onSaved();
    } else {
      await api.patch(`/admin/users/${user.id}`, { role: f.role, status: f.status, full_name: f.full_name, phone: f.phone, notes: f.notes, granted_sections: f.granted_sections, denied_sections: f.denied_sections });
      onSaved(); onClose();
    }
  }, isNew ? "User invited" : "User updated"); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="user-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form onSubmit={submit} className="relative bg-white rounded-2xl w-full max-w-xl p-6 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-head font-bold text-xl text-[var(--navy)] uppercase">{isNew ? "Invite Administrator" : `Edit ${user.email}`}</h2>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <TempPassword pw={temp} />
        {!temp && <>
          <div className="grid grid-cols-2 gap-3">
            {isNew && <AInput label="Email" type="email" value={f.email} onChange={set("email")} required data-testid="user-email" />}
            <AInput label="Full Name" value={f.full_name} onChange={set("full_name")} data-testid="user-name" />
            <AInput label="Phone" value={f.phone} onChange={set("phone")} />
            <ASelect label="Role" value={f.role} options={meta.roles.filter((r) => r !== "Super Admin")} onChange={set("role")} data-testid="user-role" />
            {!isNew && <ASelect label="Status" value={f.status} options={["Invited", "Active", "Suspended", "Disabled"]} onChange={set("status")} data-testid="user-status" />}
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Permissions (role defaults + overrides)</p>
            <SectionPicker all={meta.sections} roleDefault={roleDefault} granted={f.granted_sections} denied={f.denied_sections}
              onChange={({ granted, denied }) => setF({ ...f, granted_sections: granted, denied_sections: denied })} />
          </div>
          <ATextarea label="Notes" value={f.notes} onChange={set("notes")} rows={2} />
          <div className="flex gap-2 justify-end">
            <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
            <Btn type="submit" loading={saving} data-testid="user-save">{isNew ? "Invite" : "Save"}</Btn>
          </div>
        </>}
        {temp && <Btn variant="ghost" type="button" onClick={onClose} className="w-full">Done</Btn>}
      </form>
    </div>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [edit, setEdit] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/roles").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const resetPw = async (u) => { const r = await api.post(`/admin/users/${u.id}/reset-password`); window.prompt("New temporary password (copy it now):", r.data.temp_password); };
  const unlock = async (u) => { try { await api.post(`/admin/users/${u.id}/unlock`); toast.success("Login unlocked"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const del = async (u) => { if (!window.confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  if (!meta) return null;
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">Admin Users</h1>
        <Btn onClick={() => setShowNew(true)} data-testid="new-user-btn"><Plus className="w-4 h-4" /> Invite</Btn>
      </div>
      <Card>
        {users.length === 0 ? <EmptyState icon={Shield} title="No administrators" /> : (
          <div className="overflow-x-auto -m-1">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-400 text-xs uppercase border-b border-gray-100">
                <th className="py-2 px-2">User</th><th className="px-2">Role</th><th className="px-2">Status</th><th className="px-2">Last Login</th><th className="px-2 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`user-row-${u.email}`}>
                    <td className="py-3 px-2">
                      <p className="font-semibold text-[var(--navy)] flex items-center gap-1.5">{u.full_name || u.email} {u.is_super && <Lock className="w-3.5 h-3.5 text-[var(--red)]" />}</p>
                      <p className="text-xs text-gray-400">{u.email}</p>
                    </td>
                    <td className="px-2"><Badge color={u.is_super ? "red" : "navy"}>{u.role}</Badge></td>
                    <td className="px-2"><Badge color={u.status === "Active" ? "green" : u.status === "Suspended" || u.status === "Disabled" ? "red" : "amber"}>{u.status}</Badge></td>
                    <td className="px-2 text-xs text-gray-400">{u.last_login ? new Date(u.last_login).toLocaleDateString() : "Never"}</td>
                    <td className="px-2 text-right whitespace-nowrap">
                      {!u.is_super && <>
                        <button onClick={() => setEdit(u)} className="text-[var(--action)] text-xs font-semibold mr-3" data-testid={`edit-${u.email}`}>Edit</button>
                        {(u.locked_until || u.failed_attempts > 0) && <button onClick={() => unlock(u)} className="text-amber-500 hover:text-amber-700 mr-3" title="Unlock login" data-testid={`unlock-${u.email}`}><Unlock className="w-4 h-4 inline" /></button>}
                        <button onClick={() => resetPw(u)} className="text-gray-400 hover:text-[var(--navy)] mr-3" title="Reset password" data-testid={`reset-${u.email}`}><KeyRound className="w-4 h-4 inline" /></button>
                        <button onClick={() => del(u)} className="text-gray-300 hover:text-red-500" title="Delete" data-testid={`delete-${u.email}`}><Trash2 className="w-4 h-4 inline" /></button>
                      </>}
                      {u.is_super && <span className="text-[11px] text-gray-400">Protected</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {showNew && <UserModal meta={meta} onClose={() => setShowNew(false)} onSaved={load} />}
      {edit && <UserModal user={edit} meta={meta} onClose={() => setEdit(null)} onSaved={load} />}
    </div>
  );
}
